import { chatService } from "@/services";
import { ChatMessage, ChatSession } from "@/types/chat";
import { useCallback, useEffect, useState } from "react";

interface UseChatReturn {
  // Estado
  messages: ChatMessage[];
  session: ChatSession | null;
  status: "connected" | "disconnected" | "connecting" | "error";
  loading: boolean;
  error: string | null;

  // Ações
  sendMessage: (content: string) => Promise<void>;
  createNewSession: () => Promise<void>;
  loadSession: (sessionId: string) => Promise<void>;
  clearMessages: () => void;
  checkConnection: () => Promise<void>;
}

export const useChat = (): UseChatReturn => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [session, setSession] = useState<ChatSession | null>(null);
  const [status, setStatus] = useState<
    "connected" | "disconnected" | "connecting" | "error"
  >("connecting");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Verificar conexão inicial
  useEffect(() => {
    checkConnection();
  }, []);

  // Verificar conexão com o servidor
  const checkConnection = useCallback(async () => {
    try {
      setStatus("connecting");
      const isConnected = await chatService.checkConnection();
      setStatus(isConnected ? "connected" : "disconnected");
      setError(null);
    } catch (err) {
      setStatus("error");
      setError("Erro ao conectar com o servidor");
    }
  }, []);

  // Criar nova sessão
  const createNewSession = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const newSession = await chatService.createSession({
        title: `Sessão ${new Date().toLocaleString("pt-BR")}`,
        description: "Nova sessão de chat",
      });

      setSession(newSession);
      setMessages([]);

      // Adicionar mensagem de boas-vindas
      const welcomeMessage: ChatMessage = {
        id: Date.now().toString(),
        role: "assistant",
        content:
          "Olá! Sou o assistente do MCP Server. Como posso ajudá-lo hoje?",
        timestamp: new Date().toISOString(),
        sessionId: newSession.sessionId,
      };

      setMessages([welcomeMessage]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao criar sessão");
    } finally {
      setLoading(false);
    }
  }, []);

  // Carregar sessão existente
  const loadSession = useCallback(async (sessionId: string) => {
    try {
      setLoading(true);
      setError(null);

      const [sessionData, messageHistory] = await Promise.all([
        chatService.getSession(sessionId),
        chatService.getMessageHistory(sessionId),
      ]);

      setSession(sessionData);
      setMessages(messageHistory);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao carregar sessão");
    } finally {
      setLoading(false);
    }
  }, []);

  // Enviar mensagem
  const sendMessage = useCallback(
    async (content: string) => {
      if (!session) {
        setError("Nenhuma sessão ativa");
        return;
      }

      try {
        setError(null);

        // Adicionar mensagem do usuário imediatamente
        const userMessage: ChatMessage = {
          id: Date.now().toString(),
          role: "user",
          content,
          timestamp: new Date().toISOString(),
          sessionId: session.sessionId,
        };

        setMessages((prev) => [...prev, userMessage]);

        // Enviar para o servidor
        const response = await chatService.sendMessage({
          sessionId: session.sessionId,
          content,
        });

        // Adicionar resposta do assistente
        if (response.assistantMessage) {
          setMessages((prev) => [...prev, response.assistantMessage!]);
        } else if (response.response) {
          // Fallback para o campo response se não houver assistantMessage
          const assistantMessage: ChatMessage = {
            id: (Date.now() + 1).toString(),
            role: "assistant",
            content: response.response,
            timestamp: new Date().toISOString(),
            sessionId: session.sessionId,
          };
          setMessages((prev) => [...prev, assistantMessage]);
        }
      } catch (err) {
        console.error("Erro ao enviar mensagem no hook:", err);

        const errorMessage =
          err instanceof Error ? err.message : "Erro ao enviar mensagem";

        setError(errorMessage);

        // Remover mensagem do usuário em caso de erro
        setMessages((prev) =>
          prev.filter((msg) => msg.role !== "user" || msg.content !== content)
        );

        // Adicionar mensagem de erro visível ao usuário
        const errorMessageObj: ChatMessage = {
          id: (Date.now() + 2).toString(),
          role: "assistant",
          content: `❌ Erro: ${errorMessage}`,
          timestamp: new Date().toISOString(),
          sessionId: session.sessionId,
        };
        setMessages((prev) => [...prev, errorMessageObj]);
      }
    },
    [session]
  );

  // Limpar mensagens
  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  return {
    // Estado
    messages,
    session,
    status,
    loading,
    error,

    // Ações
    sendMessage,
    createNewSession,
    loadSession,
    clearMessages,
    checkConnection,
  };
};
